import {
  appealApiUrlSelector,
  apiBaseUrlSelector,
  appraisalNoticeServiceSelector,
  appraisalServicePerOfficeSelector,
  appraisalServiceSelector,
  appealsServiceSelector,
  assessmentServiceSelector,
  authApiUrlSelector,
  authenticationTokenSelector,
  conferenceServiceSelector,
  deedServiceSelector,
  equityfinderServiceSelector,
  eventServiceSelector,
  exportServiceSelector,
  gamaServiceSelector,
  hsAuditServiceSelector,
  importServiceSelector,
  integrityServiceSelector,
  litigationServiceSelector,
  microsoftServiceSelector,
  mobileServiceSelector,
  mraServiceSelector,
  officeDataSelector,
  ptdServiceSelector,
  publicServiceSelector,
  queueServiceSelector,
  recalcApiUrlSelector,
  recalculationServiceSelector,
  reportServiceSelector,
  s3ServiceSelector,
  sketchServiceSelector,
  taxingUnitServiceSelector,
  taxTransparencyServiceSelector,
  userServiceSelector,
  virtualHelpServiceSelector,
} from '../selectors/global';
import { selectedPropertyRecordSelector } from '../modules/appraisal/selectors/global';
import { WARNING } from './global';

export function makeApiActionCreator(
  _apiCall,
  startActionType,
  successActionType,
  errorActionType,
) {
  return (params) => {
    return async (dispatch, getState) => {
      const state = getState();
      const apiBaseUrl = apiBaseUrlSelector(state);
      const authApiUrl = authApiUrlSelector(state);
      const authenticationToken = authenticationTokenSelector(state);
      dispatch({ ...params, type: startActionType });
      try {
        const data = await _apiCall(
          apiBaseUrl,
          authenticationToken,
          params,
          authApiUrl,
        );
        dispatch({ ...params, data, type: successActionType });
        return data;
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        if (error.statusCode === 409) {
          dispatch({ ...params, error, type: WARNING });
          dispatch({ ...params, error, type: errorActionType });
        } else if (error.statusCode === 403) {
          dispatch({ type: WARNING, error: { body: { warning: error.body } } });
          dispatch({ ...params, error, type: errorActionType });
        } else if (error.statusCode === 401) {
          dispatch({ type: WARNING, error: { body: { warning: error.body } } });
          dispatch({ ...params, error, type: errorActionType });
        } else {
          dispatch({ ...params, error, type: errorActionType });
        }
        throw error;
      }
    };
  };
}

export function makeApiActionCreatorV2(
  _apiCall,
  startActionType,
  successActionType,
  errorActionType,
) {
  return (params) => {
    return async (dispatch, getState) => {
      const state = getState();
      const apiBaseUrl = apiBaseUrlSelector(state);
      const authApiUrl = authApiUrlSelector(state);
      const recalcApiUrl = recalcApiUrlSelector(state);
      const appealApiUrl = appealApiUrlSelector(state);
      const authenticationToken = authenticationTokenSelector(state);
      dispatch({ ...params, type: startActionType });
      try {
        const data = await _apiCall(
          { apiBaseUrl, authApiUrl, recalcApiUrl, appealApiUrl },
          authenticationToken,
          params,
        );
        dispatch({ ...params, data, type: successActionType });
        return data;
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        if (error.statusCode === 409) {
          dispatch({ ...params, error, type: WARNING });
          dispatch({ ...params, error, type: errorActionType });
        } else if (error.statusCode === 403) {
          dispatch({ type: WARNING, error: { body: { warning: error.body } } });
          dispatch({ ...params, error, type: errorActionType });
        } else if (error.statusCode === 401) {
          dispatch({ type: WARNING, error: { body: { warning: error.body } } });
          dispatch({ ...params, error, type: errorActionType });
        } else {
          dispatch({ ...params, error, type: errorActionType });
        }
        throw error;
      }
    };
  };
}

export function makeApiActionCreatorV3(
  _apiCall,
  startActionType,
  successActionType,
  errorActionType,
  discardOnPropertyChange = false,
) {
  return (params) => {
    return async (dispatch, getState) => {
      const state = getState();
      const office = officeDataSelector(state);
      const appealsService = appealsServiceSelector(state);
      const publicService = publicServiceSelector(state);
      const userService = userServiceSelector(state);
      const taxingUnitService = taxingUnitServiceSelector(state);
      const taxTransparencyService = taxTransparencyServiceSelector(state);
      const recalculationService = recalculationServiceSelector(state);
      const microsoftService = microsoftServiceSelector(state);
      const virtualHelpService = virtualHelpServiceSelector(state);
      const conferenceService = conferenceServiceSelector(state);
      const sketchService = sketchServiceSelector(state);
      const appraisalServicePerOffice =
        appraisalServicePerOfficeSelector(state);
      const appraisalServicePerOfficeJS = appraisalServicePerOffice
        ? appraisalServicePerOffice.toJS()
        : {};
      const appraisalService =
        appraisalServicePerOfficeJS[office] || appraisalServiceSelector(state);

      const assessmentService = assessmentServiceSelector(state);
      const equityfinderService = equityfinderServiceSelector(state);
      const eventService = eventServiceSelector(state);
      const gamaService = gamaServiceSelector(state);
      const hsAuditService = hsAuditServiceSelector(state);
      const litigationService = litigationServiceSelector(state);
      const mraService = mraServiceSelector(state);
      const appraisalNoticeService = appraisalNoticeServiceSelector(state);
      const reportService = reportServiceSelector(state);
      const s3Service = s3ServiceSelector(state);
      const deedService = deedServiceSelector(state);
      const queueService = queueServiceSelector(state);
      const integrityService = integrityServiceSelector(state);
      const mobileService = mobileServiceSelector(state);
      const ptdService = ptdServiceSelector(state);
      const exportService = exportServiceSelector(state);
      const importService = importServiceSelector(state);
      const authenticationToken = authenticationTokenSelector(state);
      const propertyContextPre = selectedPropertyRecordSelector(state);

      dispatch({ ...params, type: startActionType });
      try {
        const data = await _apiCall(
          {
            appealsService,
            publicService,
            userService,
            taxingUnitService,
            taxTransparencyService,
            recalculationService,
            microsoftService,
            virtualHelpService,
            conferenceService,
            sketchService,
            appraisalService,
            assessmentService,
            equityfinderService,
            eventService,
            gamaService,
            hsAuditService,
            litigationService,
            mraService,
            appraisalNoticeService,
            reportService,
            s3Service,
            deedService,
            queueService,
            integrityService,
            mobileService,
            ptdService,
            exportService,
            importService,
          },
          authenticationToken,
          params,
        );
        const propertyContextPost = selectedPropertyRecordSelector(getState());

        if (
          discardOnPropertyChange &&
          propertyContextPre !== propertyContextPost
        ) {
          return console.log(
            `${successActionType}: The property context changed during API request, discarding results.`,
          );
        }

        dispatch({ ...params, data, type: successActionType });
        return data;
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        if (error.statusCode === 409) {
          dispatch({ ...params, error, type: WARNING });
          dispatch({ ...params, error, type: errorActionType });
        } else if (error.statusCode === 403) {
          dispatch({ type: WARNING, error: { body: { warning: error.body } } });
          dispatch({ ...params, error, type: errorActionType });
        } else if (error.statusCode === 401) {
          dispatch({ type: WARNING, error: { body: { warning: error.body } } });
          dispatch({ ...params, error, type: errorActionType });
        } else {
          dispatch({ ...params, error, type: errorActionType });
        }
        throw error;
      }
    };
  };
}
